import { useState } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { useToast, humanizeError } from '../shared/Toast'
import TransportationField from './TransportationField'
import { decodeTransport, encodeTransport } from '../../lib/transportation'

export default function AppointmentForm({ recipientId, appointment, onDone, onCancel }) {
  const { user } = useAuth()
  const toast = useToast()
  const editing = !!appointment
  // pre_appointment_notes may hold prose (legacy) or the transportation JSON.
  const pre = decodeTransport(appointment?.pre_appointment_notes)
  const [transport, setTransport] = useState({
    transportation: pre.transportation,
    assigned_to_name: pre.assigned_to_name,
  })
  const [f, setF] = useState({
    doctor_name: appointment?.doctor_name || '',
    specialty: appointment?.specialty || '',
    location: appointment?.location || '',
    appointment_date: appointment?.appointment_date || '',
    appointment_time: appointment?.appointment_time?.slice(0, 5) || '',
    purpose: appointment?.purpose || '',
    pre_appointment_notes: pre.notes,
    post_appointment_notes: appointment?.post_appointment_notes || '',
    action_items: appointment?.action_items || '',
  })
  const [busy, setBusy] = useState(false)
  const set = (k, v) => setF((c) => ({ ...c, [k]: v }))

  async function save() {
    if (!f.doctor_name.trim()) return toast.error('Doctor name is required.')
    if (!f.appointment_date) return toast.error('Please pick a date.')
    setBusy(true)
    try {
      const payload = {
        doctor_name: f.doctor_name.trim(),
        specialty: f.specialty.trim() || null,
        location: f.location.trim() || null,
        appointment_date: f.appointment_date,
        appointment_time: f.appointment_time || null,
        purpose: f.purpose.trim() || null,
        pre_appointment_notes: encodeTransport({
          transportation: transport.transportation,
          assigned_to_name: transport.assigned_to_name,
          notes: f.pre_appointment_notes,
        }),
        post_appointment_notes: f.post_appointment_notes.trim() || null,
        action_items: f.action_items.trim() || null,
      }
      const res = editing
        ? await supabase.from('appointments').update(payload).eq('id', appointment.id)
        : await supabase.from('appointments').insert({ ...payload, care_recipient_id: recipientId, created_by: user.id })
      if (res.error) throw res.error
      toast.success(editing ? 'Appointment updated.' : 'Appointment added.')
      onDone()
    } catch (err) {
      toast.error(humanizeError(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div onClick={() => !busy && onCancel()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--bg)', width: '100%', maxWidth: 'var(--maxw)', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 20, maxHeight: '92dvh', overflowY: 'auto' }}>
        <div className="spread" style={{ marginBottom: 12 }}>
          <h2 className="title" style={{ fontSize: 20 }}>{editing ? 'Edit appointment' : 'New appointment'}</h2>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--muted)' }}>✕</button>
        </div>
        <div className="stack">
          <div className="field"><label className="label">Doctor *</label><input className="input" value={f.doctor_name} onChange={(e) => set('doctor_name', e.target.value)} placeholder="e.g. Dr. Patel" /></div>
          <div className="row" style={{ gap: 10 }}>
            <div className="field grow"><label className="label">Specialty</label><input className="input" value={f.specialty} onChange={(e) => set('specialty', e.target.value)} placeholder="e.g. Neurology" /></div>
          </div>
          <div className="field"><label className="label">Location</label><input className="input" value={f.location} onChange={(e) => set('location', e.target.value)} placeholder="Clinic / address" /></div>
          <div className="row" style={{ gap: 10 }}>
            <div className="field grow"><label className="label">Date *</label><input type="date" className="input" value={f.appointment_date} onChange={(e) => set('appointment_date', e.target.value)} /></div>
            <div className="field grow"><label className="label">Time</label><input type="time" className="input" value={f.appointment_time} onChange={(e) => set('appointment_time', e.target.value)} /></div>
          </div>
          <div className="field"><label className="label">Purpose</label><input className="input" value={f.purpose} onChange={(e) => set('purpose', e.target.value)} placeholder="What is this visit for?" /></div>
          <TransportationField
            recipientId={recipientId}
            value={transport.transportation}
            assignedName={transport.assigned_to_name}
            onChange={(transportation, assigned_to_name) => setTransport({ transportation, assigned_to_name })}
          />
          <div className="field"><label className="label">Pre-appointment notes</label><textarea className="textarea" value={f.pre_appointment_notes} onChange={(e) => set('pre_appointment_notes', e.target.value)} placeholder="What to discuss / questions to ask" /></div>

          <div className="section-title mt-8">After the visit</div>
          <div className="field"><label className="label">Post-appointment notes</label><textarea className="textarea" value={f.post_appointment_notes} onChange={(e) => set('post_appointment_notes', e.target.value)} placeholder="What was decided" /></div>
          <div className="field"><label className="label">Action items</label><textarea className="textarea" value={f.action_items} onChange={(e) => set('action_items', e.target.value)} placeholder="Follow-ups, referrals, next steps" /></div>

          <button className="btn btn-lg btn-block" onClick={save} disabled={busy}>{busy ? <span className="spinner" /> : editing ? 'Save changes' : 'Add appointment'}</button>
        </div>
      </div>
    </div>
  )
}
